import API_CONFIG from '../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';

export interface ConvergingLevel {
  timeframe: string;
  levelType: string;
  levelValue: number;
  interactionCount: number;
  bounceCount: number;
  avgStrength: number;  // Decimal 0.0-1.0 - Average signal strength from historical interactions
  distanceFromCurrent: number;
  distancePct: number;
}

export interface ConfluenceZone {
  centerPrice: number;
  minPrice: number;
  maxPrice: number;
  confluenceCount: number;
  timeframeCount: number;
  convergingLevels: ConvergingLevel[];
  distanceFromCurrent: number;
  distancePct: number;
  strengthScore: number;  // Percentage 0-100 (calculated proprietary score)
}

export interface ConfluenceZonesResponse {
  productId: string;
  currentPrice: number;
  priceRangePct: number;
  minLevels: number;
  startDate?: string;
  endDate?: string;
  confluenceZones: ConfluenceZone[];  // Backend returns 'confluenceZones', not 'zones'
  zoneCount: number;
}

export interface ConfluenceZonesParams {
  productId: string;
  startDate: Date;  // Required: start of date range (will be sent as UTC)
  endDate: Date;    // Required: end of date range (will be sent as UTC)
  pivotTimeframe?: string;  // Optional: filter to specific timeframe (e.g., "FOUR_HOUR", "DAILY")
  priceRangePct?: number;
  minLevels?: number;
  currentPrice?: number;
}

/**
 * Format a Date object as UTC ISO datetime string for API consumption.
 * Format: YYYY-MM-DDTHH:MM:SS (without Z suffix, interpreted as UTC by backend)
 */
function formatUtcDateTime(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

async function getAuthToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    return undefined;
  }
}

export async function getConfluenceZones(params: ConfluenceZonesParams): Promise<ConfluenceZonesResponse> {
  if (!params.startDate || !params.endDate) {
    throw new Error('startDate and endDate are required parameters');
  }

  const formattedStartDate = formatUtcDateTime(params.startDate);
  const formattedEndDate = formatUtcDateTime(params.endDate);

  const queryParams = new URLSearchParams({
    productId: params.productId,
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    ...(params.pivotTimeframe && { pivotTimeframe: params.pivotTimeframe }),
    ...(params.priceRangePct && { priceRangePct: params.priceRangePct.toString() }),
    ...(params.minLevels && { minLevels: params.minLevels.toString() }),
    ...(params.currentPrice && { currentPrice: params.currentPrice.toString() }),
  });

  const url = `${API_CONFIG.baseUrl}/v1/pivot-intelligence/confluence-zones?${queryParams}`;

  const token = await getAuthToken();

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch confluence zones: ${response.statusText}`);
  }

  return response.json();
}

